/*
  Мастер привязки дома к участку: участок, потом протокол собрания.

  🔴 Решение владельца 19.10.2026 (вариант В из макетов). До этого кнопка
  «Привязать к участку № 5» привязывала дом одним нажатием, и дом молча
  оставался без протокола: узнать об этом можно было по метке в таблице
  участка или отказом при сборке пакета. Теперь та же кнопка открывает окно из
  двух шагов:

  1. Судебный участок - уже выбран в таблице, но его можно поменять здесь же.
  2. Протокол общего собрания - номер и дата. Протокола под рукой нет -
     «Пропустить», и кнопка прямо называет последствие.

  Привязка уходит ОДНИМ запросом с протоколом (`POST .../buildings`, поле
  `management`): два запроса подряд оставили бы дом привязанным без протокола,
  если второй не прошёл, а человек считал бы, что сохранил оба.

  Отказ сервера показывается внутри окна, и окно не закрывается (чек-лист
  формы, пункт 1). Компонент ничего не грузит сам: привязку и перечитывание
  справочника держит `SettingsCourtSections`.
*/

import { useState } from 'react'

import { Field, Notice } from './Common.jsx'
import Modal from './Modal.jsx'
import {
  EMPTY_PROTOCOL,
  PROTOCOL_TITLE,
  ProtocolFields,
  isEmptyProtocol,
  protocolBody,
} from './ProtocolStep.jsx'

const BIND_TITLE = 'Привязать дом к участку'
const SKIP_TITLE = 'Пропустить - заявления по дому не соберутся'

/*
  `askFullAddress` (02.11.2026, вкладка «Подсудность домов»): в записи выгрузки нет
  населённого пункта, а своего поля полного адреса у той строки нет - адрес спрашивает
  первый шаг. Без него сервер не привяжет дом: заявление напечатало бы адрес, по
  которому суд дома не найдёт.
*/
export default function BindWizard({
  house,
  fullAddress,
  askFullAddress = false,
  sections,
  sectionId,
  onAttach,
  onClose,
}) {
  const [step, setStep] = useState(1)
  // Без участка из таблицы (вкладка «Подсудность домов») единственный выбирается сам.
  const [chosen, setChosen] = useState(
    sectionId ?? (sections.length === 1 ? sections[0].id : null),
  )
  const [full, setFull] = useState(fullAddress ?? '')
  const [values, setValues] = useState(EMPTY_PROTOCOL)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState(null)

  const section = sections.find((court) => court.id === chosen)
  const ready = Boolean(section) && (!askFullAddress || full.trim())

  async function attach(management) {
    setBusy(true)
    setFailure(null)
    try {
      await onAttach(chosen, {
        address: house.address,
        full_address: full.trim() || null,
        expected_premises: house.premises_count,
        management,
      })
      onClose()
    } catch (error) {
      setFailure(error)
      setBusy(false)
    }
  }

  return (
    <Modal
      title={step === 1 ? BIND_TITLE : PROTOCOL_TITLE}
      note={`${fullAddress || house.address} · помещений: ${house.premises_count}`}
      onClose={onClose}
      busy={busy}
    >
      <Steps step={step} section={section} />
      {step === 1 ? (
        <>
          {sections.length ? (
            <SectionChoice
              sections={sections}
              chosen={chosen}
              preselected={sectionId}
              onChoose={setChosen}
            />
          ) : (
            <p className="muted">
              Ваших участков ещё нет. Добавьте участок на вкладке «Участки мировых судей» и
              вернитесь к этому дому.
            </p>
          )}
          {askFullAddress ? (
            <Field label="Полный адрес дома" width="l">
              <input
                className="field__input"
                value={full}
                onChange={(event) => setFull(event.target.value)}
                placeholder={`г. Кемерово, ${house.address}`}
              />
            </Field>
          ) : null}
          <div className="row row--tight">
            <button className="button" type="button" disabled={!ready} onClick={() => setStep(2)}>
              Дальше
            </button>
            <button className="button button--quiet" type="button" onClick={onClose}>
              Отмена
            </button>
          </div>
        </>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault()
            attach(protocolBody(values))
          }}
        >
          <ProtocolFields values={values} onChange={setValues} />
          <Notice error={failure} />
          <div className="row row--tight protocol-actions">
            <button className="button" type="submit" disabled={busy || isEmptyProtocol(values)}>
              Привязать и сохранить протокол
            </button>
            <button className="button button--quiet" type="button" onClick={() => setStep(1)}>
              Назад
            </button>
          </div>
          <div className="bind-skip">
            <button
              className="link-button bind-skip__button"
              type="button"
              disabled={busy}
              onClick={() => attach({})}
            >
              {SKIP_TITLE}
            </button>
            <div className="muted bind-skip__note">
              Дом привяжется к участку, но останется в таблице участка с красной меткой «нет
              протокола» и в счёте домов без протокола на странице «Загрузка». Внести протокол
              можно будет позже кнопкой в строке дома.
            </div>
          </div>
        </form>
      )}
    </Modal>
  )
}

/*
  Лента из двух шагов - тот же вид, что у шагов загрузки (`flow`). Шаги здесь
  не нажимаются: назад ведёт кнопка «Назад», а вперёд без выбранного участка
  идти некуда.
*/
function Steps({ step, section }) {
  const second = step === 2
  return (
    <ol className="flow flow--modal" aria-label="Шаги привязки">
      <li
        className="flow__item"
        data-state={second ? 'done' : undefined}
        data-current={second ? undefined : ''}
      >
        <span className="flow__pick flow__pick--still">
          <span className="flow__dot" aria-hidden="true">
            {second ? '✓' : '1'}
          </span>
          <span className="flow__title">Судебный участок</span>
          <span className="flow__note">
            {second && section ? `№ ${section.number}` : 'кому подаём'}
          </span>
        </span>
      </li>
      <li
        className="flow__item"
        data-state={second ? undefined : 'later'}
        data-current={second ? '' : undefined}
        style={second ? { '--flow-fill': '100%' } : undefined}
      >
        <span className="flow__pick flow__pick--still">
          <span className="flow__dot" aria-hidden="true">
            2
          </span>
          <span className="flow__title">Протокол собрания</span>
          <span className="flow__note">на каком основании</span>
        </span>
      </li>
    </ol>
  )
}

function SectionChoice({ sections, chosen, preselected, onChoose }) {
  return (
    <div className="court-choice" role="radiogroup" aria-label="Судебный участок">
      {sections.map((court) => (
        <label
          key={court.id}
          className={
            court.id === chosen
              ? 'court-choice__option court-choice__option--on'
              : 'court-choice__option'
          }
        >
          <input
            className="court-choice__radio"
            type="radio"
            name="bind-court"
            checked={court.id === chosen}
            onChange={() => onChoose(court.id)}
          />
          <span>
            № {court.number} - {court.name}
            <small className="court-choice__judge">
              {court.judge_name}
              {court.id === preselected ? ' · выбран в таблице' : ''}
            </small>
          </span>
        </label>
      ))}
    </div>
  )
}
